import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import type { ColumnConfig, TableRowData, CustomCellContext } from '../types';
import { getLeafColumns } from '../utils/columnGroupUtils';

/**
 * Converts ColumnConfig definitions to TanStack Table ColumnDef format.
 *
 * When a column has children, the leaf columns are flattened into individual
 * TanStack columns (since they define accessor and renderCell for cells).
 * Group headers are rendered separately by TableHeader.
 *
 * @example
 * ```tsx
 * const columnConfigs: ColumnConfig<User>[] = [
 *   { id: 'name', header: 'Name', accessor: 'name' },
 *   {
 *     id: 'platforms',
 *     header: 'IDEs/Platform',
 *     children: [
 *       { id: 'platforms.covered', header: 'Covered', accessor: 'clients', renderCell: ... },
 *       { id: 'platforms.supported', header: 'Supported', accessor: 'clients', renderCell: ... },
 *     ],
 *   },
 * ];
 *
 * const columns = useDataTableColumns(columnConfigs);
 * // Returns 3 TanStack columns: name, platforms.covered, platforms.supported
 * ```
 */
export function useDataTableColumns<TRow extends TableRowData>(
  columnConfigs: ColumnConfig<TRow>[]
): ColumnDef<TRow>[] {
  return useMemo(() => {
    return getLeafColumns(columnConfigs).map(config =>
      convertColumnConfig(config)
    );
  }, [columnConfigs]);
}

function convertColumnConfig<TRow extends TableRowData>(
  config: ColumnConfig<TRow>
): ColumnDef<TRow> {
  const {
    accessor,
    description,
    renderCell,
    width,
    minWidth,
    maxWidth,
    ...baseConfig
  } = config;

  // Build the column definition with accessor handling
  const columnDef: ColumnDef<TRow> = {
    ...baseConfig,
    ...getAccessorDefinition(accessor),
  };

  if (typeof width === 'number') {
    columnDef.size = width;
  }

  if (typeof minWidth === 'number') {
    columnDef.minSize = minWidth;
  }

  if (typeof maxWidth === 'number') {
    columnDef.maxSize = maxWidth;
  }

  // Convert renderCell to TanStack cell format if provided
  if (renderCell) {
    columnDef.cell = createCustomCellRenderer(renderCell, config);
  }

  // Add metadata if description is provided
  if (description !== undefined) {
    columnDef.meta = { ...columnDef.meta, description };
  }

  return columnDef;
}

function getAccessorDefinition<TRow extends TableRowData>(
  accessor?: keyof TRow | ((row: TRow) => unknown)
): Partial<ColumnDef<TRow>> {
  if (!accessor) {
    return {};
  }
  if (typeof accessor === 'function') {
    return { accessorFn: accessor as (row: TRow) => unknown };
  }
  return { accessorKey: accessor as keyof TRow };
}

function createCustomCellRenderer<TRow extends TableRowData>(
  customCell: (context: CustomCellContext<TRow>) => React.ReactNode,
  config: ColumnConfig<TRow>
) {
  return (info: {
    getValue: () => unknown;
    row: { original: TRow; index: number };
  }) => {
    return customCell({
      value: info.getValue(),
      row: info.row.original,
      column: config,
      rowIndex: info.row.index,
    });
  };
}
